import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { Box, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'

import WarningDialog from '../../atoms/WarningDialog'
import useWarningDialog from '../../../hooks/useWarningDialog'

import ChoiceLabel from './ChoiceLabel'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'

export default function CarCardChoices({
  variant = VARIANT_LIST,
  car,
  selectedChoices,
  disabledChoices = {},
  blindOffersRemaining,
  selection = 'none',
  onSelectionChange = () => {},
  readOnly = false,
  showBlindOption = false,
}) {
  const { t } = useTranslation()
  const [localSelection, setLocalSelection] = useState(selection)
  const {
    openDialog,
    handleSubmitDialog,
    handleOpenDialog,
    handleCloseDialog,
  } = useWarningDialog(() => {})

  useEffect(() => {
    setLocalSelection(selection)
  }, [selection])

  const handleLocalChange = async (e) => {
    const nextSelection = e.target.value
    // INFO get feedback from backend, checking quota as from multiple sessions, before accepting and rendering option change
    const success = await onSelectionChange(car, nextSelection)
    if (success !== false) {
      setLocalSelection(nextSelection)
    } else {
      handleOpenDialog()
    }
  }

  const isDetails = variant === VARIANT_DETAILS

  const preferenceCount = Number(
    selectedChoices?.first ??
      selectedChoices?.firstChoiceCount ??
      car?.firstChoiceCount
  )
  const showPreferenceCount = Number.isFinite(preferenceCount)

  const blindOfferCount = showBlindOption
    ? Number(
        selectedChoices?.blindCount ??
          selectedChoices?.blindOffers ??
          car?.blindOfferCount
      )
    : null

  const hasAtLeastOneBlindOffer =
    showBlindOption &&
    blindOfferCount !== null &&
    !Number.isNaN(blindOfferCount) &&
    blindOfferCount > 0

  const hasRemainingBlindOffers =
    blindOffersRemaining === null ||
    blindOffersRemaining === undefined ||
    Number(blindOffersRemaining) > 0
  const hasExistingBlindPrice =
    car?.blindPrice !== null &&
    car?.blindPrice !== undefined &&
    car?.blindPrice !== ''

  const disableSelectedChoice =
    (disabledChoices?.firstChoice && localSelection !== 'selected') || readOnly
  const disableBlindChoice =
    !showBlindOption ||
    readOnly ||
    (!hasRemainingBlindOffers && !hasExistingBlindPrice)
  const disableNoneChoice = readOnly

  return (
    <>
      <Stack
        alignItems="flex-start"
        sx={{ width: 1, minWidth: '15ch' }}
        data-testid="car-card-choices"
      >
        <RadioGroup
          name={`${car.plate}.radioGroup`}
          defaultValue="none"
          onChange={handleLocalChange}
        >
          <FormControlLabel
            disabled={disableNoneChoice}
            control={
              <Radio
                checked={localSelection === 'none'}
                id={`${car.plate}.none`}
                data-testid={`${car.plate}.none`}
                value="none"
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t('carsToBuy.choice.noneChoice')}
              />
            }
          />
          <Box pb={isDetails ? 0 : 1} />
          <FormControlLabel
            disabled={disableSelectedChoice}
            control={
              <Radio
                checked={localSelection === 'selected'}
                id={`${car.plate}.selected`}
                data-testid={`${car.plate}.selected`}
                value="selected"
              />
            }
            label={
              <ChoiceLabel
                variant={variant}
                label={t('carsToBuy.choice.selected')}
                sublabel={
                  showPreferenceCount
                    ? t('carsToBuy.choice.selectedChoices', {
                        number: preferenceCount,
                      })
                    : null
                }
              />
            }
          />
          <Box pb={isDetails ? 0 : 1} />
          {showBlindOption && (
            <>
              <FormControlLabel
                disabled={disableBlindChoice}
                control={
                  <Radio
                    checked={localSelection === 'blind'}
                    id={`${car.plate}.blind`}
                    data-testid={`${car.plate}.blind`}
                    value="blind"
                  />
                }
                label={
                  <ChoiceLabel
                    variant={variant}
                    label={t('carsToBuy.choice.blind')}
                    sublabel={
                      hasAtLeastOneBlindOffer
                        ? t('carsToBuy.choice.atLeastOnePartner')
                        : null
                    }
                  />
                }
              />
              <Box pb={isDetails ? 0 : 1} />
            </>
          )}
        </RadioGroup>
      </Stack>
      <WarningDialog
        title={t('common.error.notAllowedAction')}
        content={
          <>
            {t(
              'common.error.sessions.partnerChoiceForSelectedVehicleWouldHitQuota'
            )}
          </>
        }
        open={openDialog}
        handleClose={handleCloseDialog}
        handleSubmit={handleSubmitDialog}
        hideCancel={true}
        confirmLabel={t('common.actionHide')}
      />
    </>
  )
}

CarCardChoices.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  car: T.object.isRequired,
  selectedChoices: T.object,
  disabledChoices: T.object,
  selection: T.string,
  onSelectionChange: T.func,
  readOnly: T.bool,
  showBlindOption: T.bool,
  blindOffersRemaining: T.number,
}
